import { Box, Typography } from "@outschool/backpack";
import {
  LearnerNameQueryQuery,
  LearnerNameQueryQueryVariables,
  MobileParentDashboardLearnerInterestListingsQueryQuery,
  MobileParentDashboardLearnerInterestListingsQueryQueryVariables
} from "@outschool/gql-frontend-generated";
import { faChevronLeft } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { createPathWithActivity } from "@outschool/routes";
import { useLogSearchImpression } from "@outschool/ui-analytics";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ListingCarousel } from "@outschool/ui-components-website";
import {
  IconButton,
  Loading,
  TrackingPage
} from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";
import { useParams } from "react-router-dom";

import SimpleLayout from "../../layouts/SimpleLayout";
import DatePicker from "../DatePicker";
import { useParentDashboardFilters } from "../ParentDashboardFilters";
import Listing from "./Listing";

const LearnerNameQuery = gql`
  query LearnerNameQuery($learnerUid: ID!) {
    learner(learnerUid: $learnerUid) {
      name
    }
  }
`;

export const MobileParentDashboardLearnerInterestListingsQuery = gql`
  query MobileParentDashboardLearnerInterestListingsQuery(
    $skipRating: Boolean!
    $learnerUid: String!
    $searchFilters: InterestTopicSearchFilters
    $withRecentReviews: Boolean!
  ) {
    featured {
      learnerInterestTopicListings(
        learnerUid: $learnerUid
        searchFilters: $searchFilters
      ) {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

function ClassList({
  listings
}: {
  listings: MobileParentDashboardLearnerInterestListingsQueryQuery["featured"]["learnerInterestTopicListings"];
}) {
  return (
    <Box
      sx={{
        listStyle: "none"
      }}
    >
      <ul style={{ listStyle: "none" }}>
        {listings.map((listing, index) => (
          <li key={listing.activity.uid || index}>
            <ClassListListing
              listing={listing}
              listingIndex={index}
              numListings={listings.length}
            />
          </li>
        ))}
      </ul>
    </Box>
  );
}

function ClassListListing({ listing, listingIndex, numListings }: $TSFixMe) {
  const Link = useLinkComponent();
  const { trackSearchImpressionClick, searchImpressionRef } =
    useLogSearchImpression({
      listing,
      listingIndex,
      pageLocation: "PARENT_DASHBOARD"
    });
  return (
    <Box ref={searchImpressionRef}>
      <Link
        onClick={trackSearchImpressionClick}
        to={createPathWithActivity(listing.activity)}
        trackingName="Activity Impression"
        trackingViewEventName="Activity Impression"
        trackingTouchEventName="Activity Impression Click"
        trackingUniqueId={listing.activity.uid}
        sharedProperties={{
          insertion_id: listing.insertionId,
          activity_uid: listing.activity.uid,
          page_name: "parent_dashboard_learner_interest_listings",
          order: listingIndex + 1,
          numListings
        }}
        useLinkTracking
        trackViews={true}
      >
        <Listing listing={listing} showSaved />
        {listingIndex + 1 !== numListings && (
          <hr style={{ marginTop: "28px", marginBottom: "28px" }} />
        )}
      </Link>
    </Box>
  );
}
export default function LearnerInterestsList() {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\lists\\LearnerInterestsList"
  );
  const { currentUser, userTimeZone } = useSession();
  const { learner_uid } = useParams<{ learner_uid: string }>();

  const { searchFilters } = useParentDashboardFilters();
  const { data: learnerData } = useQueryWithPreviousData<
    LearnerNameQueryQuery,
    LearnerNameQueryQueryVariables
  >(LearnerNameQuery, {
    variables: { learnerUid: learner_uid }
  });
  const learnerName = learnerData?.learner?.name;

  const { loading, data } = useQueryWithPreviousData<
    MobileParentDashboardLearnerInterestListingsQueryQuery,
    MobileParentDashboardLearnerInterestListingsQueryQueryVariables
  >(MobileParentDashboardLearnerInterestListingsQuery, {
    variables: {
      skipRating: false,
      withRecentReviews: false,
      learnerUid: learner_uid,
      searchFilters: { ...searchFilters, timeZone: userTimeZone }
    }
  });
  const listings = data?.featured?.learnerInterestTopicListings;

  return (
    <TrackingPage
      // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
      parent={currentUser}
      name="parent_dashboard_list"
      uniqueId="parent_dashboard_learner_interests_list"
      uniqueProperties={{ learner_uid }}
      useLinkTracking
    >
      {setImpressionNode => (
        // @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message
        <SimpleLayout ref={setImpressionNode} hideFooter>
          <IconButton
            icon={faChevronLeft}
            screenReaderLabel={t("Go back to dashboard")}
            onClick={() => window.history.back()}
            sx={{ marginLeft: "8px" }}
          />
          <Typography
            variant="h3"
            sx={{
              paddingX: "24px",
              fontWeight: "fontWeightBold",
              marginBottom: "34px"
            }}
          >
            {t("Classes for {{learnerName}}", { learnerName })}
          </Typography>
          <Box
            sx={{
              marginTop: "-24px",
              marginBottom: "24px"
            }}
          >
            <DatePicker />
          </Box>
          <Box
            sx={{
              paddingX: "24px"
            }}
          >
            {loading ? (
              <Loading />
            ) : // @ts-ignore TS(2532): Object is possibly 'undefined'.
            listings?.length > 0 ? (
              // @ts-ignore TS(2322): Type '{ __typename?: "Listing" | undefined; insert... Remove this comment to see the full error message
              <ClassList listings={listings} />
            ) : (
              <div>{t("No upcoming classes found.")}</div>
            )}
          </Box>
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}
